import React, { useState, useEffect, useCallback } from "react"; // 1. Import useCallback
import { FiSave, FiX, FiTrash2, FiPlus } from "react-icons/fi";
import "./AssignmentModal.scss";

const AssignmentModal = ({
  isOpen,
  onClose,
  availableUsers,
  assignedUsers,
  onSave,
  onDeleteUser,
  onDeleteAll,
  role,
  mode = "edit",
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [modalMode, setModalMode] = useState(mode);

  useEffect(() => {
    if (isOpen) {
      setSelectedUserIds(assignedUsers.map((user) => user.id));
      setModalMode(mode);
    }
  }, [isOpen, assignedUsers, mode]);

  const handleSave = useCallback(() => {
    onSave(selectedUserIds);
    onClose();
  }, [onSave, selectedUserIds, onClose]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if the pressed key is 'Enter' and if the modal is in a saveable state
      if (event.key === 'Enter' && (modalMode === "edit" || modalMode === "assign")) {
        handleSave();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleSave, modalMode]); 

  const handleUserSelectionChange = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // const handleSave = () => {
  //   onSave(selectedUserIds);
  //   onClose();
  // };

  const handleDeleteIndividualUser = (assignmentId, userName) => {
    onDeleteUser(assignmentId, userName);
  };

  const handleDeleteAll = () => {
    onDeleteAll();
    onClose();
  };

  const handleSwitchToDeleteMode = () => {
    setModalMode("delete");
  };

  const handleSwitchToEditMode = () => {
    setModalMode("edit");
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="assignment-modal-overlay" onClick={handleOverlayClick}>
      <div className="assignment-modal">
        <div className="assignment-modal-header">
          <h3>
            {modalMode === "delete" ? "Manage Assignments" : "Assign Lawyers"}
          </h3>
          <button className="assignment-modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <div className="assignment-modal-content">
          {modalMode === "edit" || modalMode === "assign" ? (
            <>
              <div className="assignment-modal-section">
                <h4>Select Lawyers to Assign:</h4>
                <div className="user-selection-container">
                  {availableUsers.map((user) => (
                    <label key={user._id} className="user-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user._id)}
                        onChange={() => handleUserSelectionChange(user._id)}
                      />
                      <span className="user-name">{user.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {assignedUsers.length > 0 && modalMode === "edit" && (
                <div className="assignment-modal-section">
                  <div className="section-header">
                    <h4>Currently Assigned:</h4>
                    <button
                      className="assignment-switch-mode-btn"
                      onClick={handleSwitchToDeleteMode}
                    >
                      <FiTrash2 size={14} /> Manage Assignments
                    </button>
                  </div>
                  <div className="current-assignments">
                    {assignedUsers.map((user, index) => (
                      <div
                        key={user.assignmentId}
                        className="assigned-user-item"
                      >
                        <span className="user-number">{index + 1}.</span>
                        <span className="user-name">{user.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="assignment-modal-section">
              <div className="section-header">
                <h4>Manage Assigned Lawyers:</h4>
                <button
                  className="assignment-switch-mode-btn"
                  onClick={handleSwitchToEditMode}
                >
                  <FiPlus size={14} /> Add Assignments
                </button>
              </div>
              <div className="delete-assignments-container">
                {assignedUsers.map((user, index) => (
                  <div key={user.assignmentId} className="delete-user-item">
                    <span className="user-number">{index + 1}.</span>
                    <span className="user-name">{user.name}</span>
                    <button
                      onClick={() =>
                        handleDeleteIndividualUser(user.assignmentId, user.name)
                      }
                      className="assignment-delete-individual-btn"
                      title={`Remove ${user.name}`}
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="assignment-modal-footer">
          {modalMode === "edit" || modalMode === "assign" ? (
            <>
              <button onClick={handleSave} className="assignment-save-btn">
                <FiSave size={14} /> Save Assignments
              </button>
              <button onClick={onClose} className="assignment-cancel-btn">
                <FiX size={14} /> Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleDeleteAll}
                className="assignment-delete-all-btn"
              >
                <FiTrash2 size={14} /> Remove All Assignments
              </button>
              <button onClick={onClose} className="assignment-cancel-btn">
                <FiX size={14} /> Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;